import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, BookText, Search, AlertTriangle, Ruler, Landmark } from "lucide-react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";

export default function CodeResearch({ open, onOpenChange, state, setState, city, setCity, research, setResearch }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const run = async () => {
    if (!state && (!city || city === "Custom")) { toast.error("Enter at least a state or city"); return; }
    setLoading(true);
    try {
      const res = await api.post("/ai/research-codes", { state, city });
      setResearch(res.data);
      toast.success("Local code requirements researched");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Research failed");
    } finally { setLoading(false); }
  };

  const sendToOrdinance = async () => {
    if (!research) return;
    const s = research.setbacks_ft || {};
    setSaving(true);
    try {
      await api.post("/ordinances", {
        city_name: research.jurisdiction || [city, state].filter(Boolean).join(", ") || "Researched",
        rules: {
          max_height_ft: research.max_height_ft || 35,
          front_setback_ft: s.front || 20,
          side_setback_ft: s.side || 5,
          rear_setback_ft: s.rear || 15,
          max_lot_coverage_pct: research.max_lot_coverage_pct || 50,
          max_far: 0.6,
          max_bedrooms: 6,
          min_egress_window_sqft: 5.7,
        },
      });
      toast.success("Saved to Ordinance Checker — run a compliance check to flag violations");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save ordinance");
    } finally { setSaving(false); }
  };

  const sb = research?.setbacks_ft || {};
  const stat = (label, val, unit = "") => (
    <div className="bg-[#0D1117] border border-[#30363D] rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-widest text-[#6E7681] font-mono-cad">{label}</div>
      <div className="text-lg font-heading font-semibold text-[#F0F6FC]">{val ?? "—"}{val !== undefined && val !== null ? unit : ""}</div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#161B22] border-[#30363D] text-[#F0F6FC] max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><BookText className="w-5 h-5 text-[#F59E0B]" /> Jurisdiction & Code Research</DialogTitle>
          <DialogDescription className="text-[#8B949E]">Set the state and city, and AI researches the applicable residential zoning & building code (setbacks, height, coverage) before you design.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#8B949E]">State</Label>
              <Input value={state} onChange={(e) => setState(e.target.value)} placeholder="e.g. Colorado" className="bg-[#0D1117] border-[#30363D] h-9" data-testid="code-state-input" />
            </div>
            <div>
              <Label className="text-xs text-[#8B949E]">City / County</Label>
              <Input value={city === "Custom" ? "" : city} onChange={(e) => setCity(e.target.value || "Custom")} placeholder="e.g. Denver" className="bg-[#0D1117] border-[#30363D] h-9" data-testid="code-city-input" />
            </div>
          </div>
          <Button onClick={run} disabled={loading} data-testid="code-research-btn" className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-[#0D1117] font-semibold">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Researching code book…</> : <><Search className="w-4 h-4 mr-2" /> Research Local Codes</>}
          </Button>

          {research && (
            <div className="space-y-4" data-testid="code-research-results">
              <div className="rounded-lg border border-[#F59E0B]/30 bg-[#0D1117] p-3">
                <div className="text-sm font-heading font-semibold text-[#F59E0B]">{research.jurisdiction}</div>
                {research.code_basis && <div className="text-xs text-[#8B949E] font-mono-cad mt-0.5">{research.code_basis}</div>}
                {research.summary && <p className="text-sm text-[#C9D1D9] mt-2">{research.summary}</p>}
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-widest text-[#6E7681] font-mono-cad mb-2 flex items-center gap-1.5"><Ruler className="w-3.5 h-3.5" /> Setbacks & Envelope</div>
                <div className="grid grid-cols-3 gap-2">
                  {stat("Front Setback", sb.front, "′")}
                  {stat("Side Setback", sb.side, "′")}
                  {stat("Rear Setback", sb.rear, "′")}
                  {stat("Max Height", research.max_height_ft, "′")}
                  {stat("Max Stories", research.max_stories)}
                  {stat("Lot Coverage", research.max_lot_coverage_pct, "%")}
                  {stat("Min Lot Size", research.min_lot_size_sqft, " ft²")}
                  {stat("Parking", research.parking_spaces, " sp")}
                </div>
                <Button onClick={sendToOrdinance} disabled={saving} data-testid="code-to-ordinance-btn" className="w-full mt-2 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold">
                  {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Landmark className="w-4 h-4 mr-2" /> Send to Ordinance Checker</>}
                </Button>
                <p className="text-[11px] text-[#6E7681] mt-1.5">Also toggle <span className="text-[#F59E0B]">Setbacks</span> in the toolbar to draw the buildable envelope on the plan.</p>
              </div>

              {research.codes?.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-[#6E7681] font-mono-cad mb-2">Key Code Items</div>
                  <div className="space-y-1.5" data-testid="code-items-list">
                    {research.codes.map((c, i) => (
                      <div key={i} className="bg-[#0D1117] border border-[#30363D] rounded-md p-2.5">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono-cad text-[#38BDF8]">{c.name}</span>
                          <span className="text-[10px] text-[#6E7681]">{c.topic}</span>
                        </div>
                        <div className="text-sm text-[#C9D1D9] mt-1">{c.requirement}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {research.notes?.length > 0 && (
                <ul className="text-xs text-[#8B949E] space-y-1 list-disc pl-4">
                  {research.notes.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              )}

              <div className="flex items-start gap-2 text-[11px] text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-md p-2.5">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>AI-generated starting point only — always confirm exact requirements with the local Authority Having Jurisdiction (AHJ) before permitting.</span>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
